import React, { useState, useEffect } from 'react';
import API from '../../api';
import { toast } from 'react-toastify';

export default function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  
  // Activate Form
  const [activateForm, setActivateForm] = useState({
    userId: '',
    planId: '',
    notes: ''
  });
  
  // Extend Form
  const [extendForm, setExtendForm] = useState({
    daysToExtend: 30,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch subscriptions
      const subRes = await API.get(`/subscriptions/all${filter !== 'all' ? `?status=${filter}` : ''}`);
      setSubscriptions(subRes.data.subscriptions || []);
      
      // Fetch stats
      const statsRes = await API.get('/subscriptions/stats');
      setStats(statsRes.data.stats);
      
      // Fetch users and plans for activate modal
      const usersRes = await API.get('/users/admin/users');
      setUsers(usersRes.data || []);
      
      const plansRes = await API.get('/subscriptions/plans');
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load subscription data');
    }
    setLoading(false);
  };

  const handleActivateSubscription = async (e) => {
    e.preventDefault();
    try {
      await API.post('/subscriptions/admin/activate', activateForm);
      toast.success('Subscription activated successfully');
      setShowActivateModal(false);
      setActivateForm({ userId: '', planId: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to activate subscription');
    }
  };

  const handleExtendSubscription = async (e) => {
    e.preventDefault();
    try {
      await API.post('/subscriptions/admin/extend', {
        subscriptionId: selectedSubscription._id,
        ...extendForm
      });
      toast.success('Subscription extended successfully');
      setShowExtendModal(false);
      setExtendForm({ daysToExtend: 30, notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to extend subscription');
    }
  };

  const handleCancelSubscription = async (subscription) => {
    const reason = prompt('Enter cancellation reason (optional):');
    if (reason === null) return; // User clicked cancel
    
    try {
      await API.post('/subscriptions/admin/cancel', {
        subscriptionId: subscription._id,
        reason: reason || 'Cancelled by admin'
      });
      toast.success('Subscription cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel subscription');
    }
  };

  const openExtendModal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowExtendModal(true);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return `Rs. ${(amount / 100).toLocaleString()}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: '#dcfce7', color: '#166534' };
      case 'expired': return { bg: '#fee2e2', color: '#991b1b' };
      case 'cancelled': return { bg: '#fef3c7', color: '#92400e' };
      case 'pending': return { bg: '#e0e7ff', color: '#3730a3' };
      default: return { bg: '#f1f5f9', color: '#475569' };
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading subscriptions...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📋 Subscription Management</h1>
          <p style={styles.subtitle}>Manage user subscriptions and payments</p>
        </div>
        <button onClick={() => setShowActivateModal(true)} style={styles.activateBtn}>
          ➕ Activate Subscription
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>✅</div>
            <div style={styles.statInfo}>
              <p style={styles.statLabel}>Active Subscriptions</p>
              <h3 style={styles.statValue}>{stats.activeSubscriptions}</h3>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>⏰</div>
            <div style={styles.statInfo}>
              <p style={styles.statLabel}>Expiring Soon (7 days)</p>
              <h3 style={styles.statValue}>{stats.expiringSoon}</h3>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>💰</div>
            <div style={styles.statInfo}>
              <p style={styles.statLabel}>Revenue This Month</p>
              <h3 style={styles.statValue}>Rs. {stats.revenueThisMonth?.toLocaleString() || 0}</h3>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>💎</div>
            <div style={styles.statInfo}>
              <p style={styles.statLabel}>Total Revenue</p>
              <h3 style={styles.statValue}>Rs. {stats.totalRevenue?.toLocaleString() || 0}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Expiring Soon Alert */}
      {stats?.usersExpiringSoon?.length > 0 && (
        <div style={styles.alertBox}>
          <h3 style={styles.alertTitle}>⚠️ Subscriptions Expiring Soon</h3>
          <div style={styles.expiringList}>
            {stats.usersExpiringSoon.slice(0, 5).map((sub) => (
              <div key={sub._id} style={styles.expiringItem}>
                <span>{sub.user?.name} ({sub.user?.email})</span>
                <span style={styles.expiringDate}>
                  Expires: {formatDate(sub.endDate)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div style={styles.filterTabs}>
        {['all', 'active', 'expired', 'cancelled', 'pending'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            style={{
              ...styles.filterTab,
              backgroundColor: filter === status ? '#667eea' : '#f1f5f9',
              color: filter === status ? '#fff' : '#64748b'
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Subscriptions Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Plan</th>
              <th style={styles.th}>Start Date</th>
              <th style={styles.th}>End Date</th>
              <th style={styles.th}>Days Left</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length === 0 ? (
              <tr>
                <td colSpan="8" style={styles.emptyRow}>
                  No subscriptions found
                </td>
              </tr>
            ) : (
              subscriptions.map((sub) => {
                const statusStyle = getStatusColor(sub.status);
                return (
                  <tr key={sub._id}>
                    <td style={styles.td}>
                      <div style={styles.userInfo}>
                        <strong>{sub.user?.name || 'N/A'}</strong>
                        <small style={styles.userEmail}>{sub.user?.email}</small>
                      </div>
                    </td>
                    <td style={styles.td}>
                      {sub.planSnapshot?.name || sub.plan?.name || 'N/A'}
                      <br />
                      <small style={styles.duration}>
                        {sub.planSnapshot?.duration || sub.plan?.duration} months
                      </small>
                    </td>
                    <td style={styles.td}>{formatDate(sub.startDate)}</td>
                    <td style={styles.td}>{formatDate(sub.endDate)}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.daysLeft,
                        color: sub.daysRemaining <= 7 ? '#dc2626' : '#059669'
                      }}>
                        {sub.daysRemaining > 0 ? `${sub.daysRemaining} days` : 'Expired'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color
                      }}>
                        {sub.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {formatPrice(sub.planSnapshot?.price || 0)}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        {sub.status === 'active' && (
                          <>
                            <button
                              onClick={() => openExtendModal(sub)}
                              style={styles.extendBtn}
                              title="Extend"
                            >
                              ➕
                            </button>
                            <button
                              onClick={() => handleCancelSubscription(sub)}
                              style={styles.cancelBtn}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </>
                        )}
                        {sub.status === 'expired' && (
                          <button
                            onClick={() => openExtendModal(sub)}
                            style={styles.reactivateBtn}
                            title="Reactivate"
                          >
                            🔄
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Activate Subscription Modal */}
      {showActivateModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>➕ Activate Subscription</h2>
              <button onClick={() => setShowActivateModal(false)} style={styles.closeBtn}>
                ✕
              </button>
            </div>
            <form onSubmit={handleActivateSubscription} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Select User *</label>
                <select
                  value={activateForm.userId}
                  onChange={(e) => setActivateForm({ ...activateForm, userId: e.target.value })}
                  required
                  style={styles.select}
                >
                  <option value="">-- Select User --</option>
                  {users.filter(u => u.role !== 'admin').map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.name} ({user.email}) - {user.status}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Plan *</label>
                <select
                  value={activateForm.planId}
                  onChange={(e) => setActivateForm({ ...activateForm, planId: e.target.value })}
                  required
                  style={styles.select}
                >
                  <option value="">-- Select Plan --</option>
                  {plans.filter(p => p.isActive).map((plan) => (
                    <option key={plan._id} value={plan._id}>
                      {plan.name} - {plan.duration} months - Rs. {plan.price / 100}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Notes</label>
                <textarea
                  value={activateForm.notes}
                  onChange={(e) => setActivateForm({ ...activateForm, notes: e.target.value })}
                  placeholder="Optional notes..."
                  rows={3}
                  style={styles.textarea}
                />
              </div>
              
              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowActivateModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Activate Subscription
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extend Subscription Modal */}
      {showExtendModal && selectedSubscription && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>➕ Extend Subscription</h2>
              <button onClick={() => setShowExtendModal(false)} style={styles.closeBtn}>
                ✕
              </button>
            </div>
            <div style={styles.subscriptionInfo}>
              <p><strong>User:</strong> {selectedSubscription.user?.name}</p>
              <p><strong>Current End Date:</strong> {formatDate(selectedSubscription.endDate)}</p>
            </div>
            <form onSubmit={handleExtendSubscription} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Days to Extend *</label>
                <input
                  type="number"
                  value={extendForm.daysToExtend}
                  onChange={(e) => setExtendForm({ ...extendForm, daysToExtend: parseInt(e.target.value) })}
                  min="1"
                  required
                  style={styles.input}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Notes</label>
                <textarea
                  value={extendForm.notes}
                  onChange={(e) => setExtendForm({ ...extendForm, notes: e.target.value })}
                  placeholder="Reason for extension..."
                  rows={3}
                  style={styles.textarea}
                />
              </div>
              
              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowExtendModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Extend Subscription
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '30px',
  },
  loadingContainer: {
    padding: '50px',
    textAlign: 'center',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    marginTop: '5px',
  },
  activateBtn: {
    padding: '12px 24px',
    backgroundColor: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  statIcon: {
    fontSize: '32px',
  },
  statInfo: {
    flex: 1,
  },
  statLabel: {
    color: '#64748b',
    fontSize: '13px',
    margin: '0 0 4px 0',
  },
  statValue: {
    color: '#1a202c',
    fontSize: '24px',
    fontWeight: '700',
    margin: 0,
  },
  alertBox: {
    backgroundColor: '#fef3c7',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '25px',
    border: '1px solid #fcd34d',
  },
  alertTitle: {
    color: '#92400e',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 15px 0',
  },
  expiringList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  expiringItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#78350f',
  },
  expiringDate: {
    fontWeight: '600',
  },
  filterTabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '25px',
    flexWrap: 'wrap',
  },
  filterTab: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '14px 16px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#1e293b',
    verticalAlign: 'middle',
  },
  emptyRow: {
    textAlign: 'center',
    padding: '40px',
    color: '#94a3b8',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  userEmail: {
    color: '#94a3b8',
    fontSize: '12px',
  },
  duration: {
    color: '#94a3b8',
    fontSize: '12px',
  },
  daysLeft: {
    fontWeight: '600',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  actionButtons: {
    display: 'flex',
    gap: '6px',
  },
  extendBtn: {
    padding: '6px 10px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  cancelBtn: {
    padding: '6px 10px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  reactivateBtn: {
    padding: '6px 10px',
    backgroundColor: '#e0e7ff',
    color: '#3730a3',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f1f5f9',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#94a3b8',
  },
  subscriptionInfo: {
    padding: '16px 24px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '14px',
  },
  form: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '10px',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '12px 24px',
    backgroundColor: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};